import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { UserRoleEnum } from '@enum/role.enum';

import { UserPermissionService } from '@services/helper/user-permission.service';

/**
 * FEC-1250 / FEC-1252 / FEC-1256: shuts a route to the Agency Manager role only.
 *
 * Every other role is let through untouched. The role is read from persisted
 * state because guards run before `/me` is fetched (see dashboardAccessGuard,
 * FEC-1171). A blocked Agency Manager is redirected to the project's Apartments
 * board when the route sits under a project, otherwise to the Projects list.
 *
 * @param onlyWhen optional predicate narrowing the block to some URLs of the
 *   route (e.g. only `/project/add`); omit to block the whole route.
 */
export function blockAgencyManagerGuard(
  onlyWhen?: (route: Parameters<CanActivateFn>[0]) => boolean,
): CanActivateFn {
  return (route) => {
    const userPermission = inject(UserPermissionService);
    const router = inject(Router);

    if (!userPermission.hasLoadedPermissions()) userPermission.initializePermissions();

    if (userPermission.getPersistedRoleName() !== UserRoleEnum.AgencyManager) return true;
    if (onlyWhen && !onlyWhen(route)) return true;

    const projectId = route.parent?.paramMap.get('id');
    return router.createUrlTree([projectId ? `/project-detail/${projectId}/view` : '/']);
  };
}

/** Narrows {@link blockAgencyManagerGuard} to the create-project URL (`/project/add`). */
export const isAddProjectRoute = (route: Parameters<CanActivateFn>[0]): boolean =>
  route.paramMap.get('id') === 'add';
